<template>
  <div class="space-y-6">
    <!-- Error Alert -->
    <div v-if="errorMessage" class="bg-red-50 border-l-4 border-red-500 p-4 rounded">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-red-800">{{ errorTitle || 'Error' }}</h3>
          <div class="mt-2 text-sm text-red-700 whitespace-pre-line">{{ errorMessage }}</div>
          <div class="mt-4">
            <button
              @click="errorMessage = null; errorTitle = null"
              class="text-sm font-medium text-red-800 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Session selection -->
    <section class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 mt-1 flex items-center gap-2">
            Live Session Overview
            <BaseInfoIcon :text="ERKLAERUNG.feedbackLiveTab" label="Live Session Overview" />
          </h2>
          <p class="text-sm text-gray-500">Browse your live session feedback and per-criterion results.</p>
        </div>
      </div>

      <div class="mt-4">
        <div v-if="!sessions.length" class="mt-6 rounded-xl border border-gray-100 bg-gray-50 px-4 py-5 text-sm text-gray-600">
          No live sessions available.
        </div>

        <div v-else class="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div class="md:w-72">
            <label class="block text-sm font-semibold text-gray-700 mb-1" for="live-session-select">Live Session</label>
            <BaseSelect
              id="live-session-select"
              v-model="selectedSessionId"
              :options="sessionOptions"
              showPlaceholder
              placeholder="Select a session"
            />
          </div>

          <div v-if="selectedSession" class="md:w-72">
            <label class="block text-sm font-semibold text-gray-700 mb-1" for="criterion-select">Filter History by Criterion</label>
            <BaseSelect
              id="criterion-select"
              v-model="selectedCriterionId"
              :options="criterionOptions"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Main session display -->
    <div v-if="selectedSession" class="mb-8 bg-gray-100 p-4 rounded-lg shadow-sm">
      <!-- Session info -->
      <div class="flex items-center gap-3 mb-2">
        <h3 class="text-xl font-semibold text-gray-800">{{ selectedSession.title }}</h3>
      </div>

      <!-- Criteria grid -->
      <div v-if="selectedSession.feedback?.length">
        <!-- Non-text criteria -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div v-for="criterion in nonTextCriteria" :key="criterion.criterion_id" class="flex justify-between items-center p-3 border rounded-lg shadow-sm bg-white">
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <p class="text-gray-900 font-medium">{{ criterion.criterion_name }}</p>
                <div v-if="criterion.criterion?.description" class="group relative">
                  <button class="ml-1 inline-flex items-center justify-center text-indigo-500 h-7 w-7 rounded-full focus:outline-none" aria-label="Show description">
                    <QuestionMarkCircleIcon class="h-5 w-5" />
                  </button>
                  <div class="invisible group-hover:visible absolute z-10 bg-gray-100 text-black text-sm rounded border border-blue-900 py-2 px-3 bottom-full left-0 mb-2 w-64 whitespace-normal shadow-sm">
                    {{ criterion.criterion?.description }}
                  </div>
                </div>
              </div>
              <span class="text-xs font-semibold px-2 py-0.5 rounded-full mt-1 self-start"
                    :class="getCriterionWeightForRole(criterion) === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'">
                Weight: {{ getCriterionWeightForRole(criterion) }}
              </span>
            </div>

            <div v-if="criterion.criterion_type === 'countable'" class="text-gray-700 font-bold text-lg">
              {{ criterion.published_value }}
              <div class="text-sm text-gray-500">
                ⌀ Avg: {{
                  (criterionAverages.find(a => a.criterion_id === criterion.criterion_id && a.role_name === userSession?.role_name)?.average_value ?? 0)
                }}
              </div>
            </div>

            <div v-else-if="criterion.criterion_type === 'boolean'">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium"
                    :class="criterion.is_fulfilled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-gray-500'">
                {{ criterion.is_fulfilled ? 'Fulfilled' : 'Not fulfilled' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Text criteria -->
        <div class="flex flex-col gap-4">
          <div v-for="criterion in textCriteria" :key="criterion.criterion_id" class="p-3 border rounded-lg shadow-sm bg-white">
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <p class="text-gray-900 font-medium">{{ criterion.criterion_name }}</p>
                <div v-if="criterion.criterion?.description" class="group relative">
                  <button class="ml-1 inline-flex items-center justify-center text-indigo-500 h-7 w-7 rounded-full focus:outline-none" aria-label="Show description">
                    <QuestionMarkCircleIcon class="h-5 w-5" />
                  </button>
                  <div class="invisible group-hover:visible absolute z-10 bg-gray-100 text-black text-sm rounded border border-blue-900 py-2 px-3 bottom-full left-0 mb-2 w-64 whitespace-normal shadow-sm">
                    {{ criterion.criterion?.description }}
                  </div>
                </div>
              </div>
              <span class="text-xs font-semibold px-2 py-0.5 rounded-full mt-1 self-start"
                    :class="getCriterionWeightForRole(criterion) === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'">
                Weight: {{ getCriterionWeightForRole(criterion) }}
              </span>
            </div>
            <div class="text-gray-800 whitespace-pre-wrap break-words mt-2">
              {{
                criterion.text_value?.length
                  ? criterion.text_value.filter(tv => tv.is_active).map(tv => tv.text_value).join('\n') || '—'
                  : '—'
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- No criteria -->
      <p v-else class="text-gray-500 text-center">No feedback available for this session.</p>

      <!-- Session feedback -->
      <div v-if="userSession" class="mt-8 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h4 class="text-lg font-semibold text-gray-800 mb-2">Session Feedback</h4>
        <div class="space-y-3">
          <!-- Grade -->
          <div>
            <p class="text-gray-700">
              <strong>Grade:</strong> 
              <span v-if="userSession.grade_released">{{ formatGrade(userSession.grade) }}</span>
              <span v-else class="text-gray-400 italic">Not released yet</span>
            </p>
          </div>
          
          <!-- Educational goal (always visible) -->
          <div>
            <p class="text-gray-700 whitespace-pre-wrap">
              <strong>Educational goal:</strong> {{ userSession.goal || '—' }}
            </p>
          </div>
          
          <!-- Evaluation -->
          <div>
            <p class="text-gray-700 whitespace-pre-wrap">
              <strong>Evaluation:</strong>
              <span v-if="userSession.evaluation_released">{{ userSession.evaluation || '—' }}</span>
              <span v-else class="text-gray-400 italic">Not released yet</span>
            </p>
          </div>
          
          <!-- Recommendation -->
          <div>
            <p class="text-gray-700 whitespace-pre-wrap">
              <strong>Recommendation:</strong>
              <span v-if="userSession.recommendation_released">{{ userSession.recommendation || '—' }}</span>
              <span v-else class="text-gray-400 italic">Not released yet</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Criterion History -->
      <section v-if="filteredHistory.length" class="mt-8 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h4 class="text-lg font-semibold text-gray-800 mb-3">Criterion Change History</h4>
        <ul class="space-y-3">
          <li v-for="(entry, idx) in filteredHistory" :key="idx" class="border border-gray-100 rounded-lg p-3 bg-gray-50 flex justify-between items-start cursor-pointer">
            <div class="flex-1">
              <p class="text-gray-700 text-sm">
                <strong>{{ new Date(entry.changed_at).toLocaleString() }}</strong>
                <span v-if="entry.source === 'team' && entry.team_name" class="font-bold text-indigo-600 ml-1">[{{ entry.team_name }}]</span>
                — 
                <span class="font-medium text-lg">{{ entry.criterion_name }}</span>
                <!-- Change value -->
                <span v-if="entry.change_value !== undefined" class="ml-2 text-sm">
                  <span v-if="entry.change_value > 0" class="text-emerald-600 font-semibold">▲ +{{ entry.change_value }}</span>
                  <span v-else-if="entry.change_value < 0" class="text-red-600 font-semibold">▼ {{ entry.change_value }}</span>
                </span>
              </p>
              <p class="text-gray-800 mt-1">
                <span v-if="entry.int_value != null">Count changed to <strong>{{ entry.int_value }}</strong></span>
                <span v-if="entry.bool_value != null">{{ entry.int_value !== null ? ' ' : '' }}Boolean set to <strong>{{ entry.bool_value }}</strong></span>
                <span v-if="entry.text_value">{{ (entry.int_value !== null || entry.bool_value !== null) ? ' ' : '' }}"<strong>{{ entry.text_value }}</strong>"</span>
                <span v-if="entry.int_value === null && entry.bool_value === null && !entry.text_value">—</span>
              </p>
              <p v-if="entry.published" class="text-xs text-emerald-700 mt-1">Published {{ new Date(entry.published_at).toLocaleString() }}</p>
            </div>
            
            <button 
              class="ml-2 p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              @click.stop="openChatWithHistory(entry)"
              title="Ask about this"
            >
              <ChatBubbleLeftIcon class="w-4 h-4" />
            </button>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import BaseSelect from '@/BaseComponents/BaseSelect.vue';
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import { ERKLAERUNG } from '@/async-sessions/erklaerungen';
import { getLiveSessions, getSessionFeedback, getUserSession } from '@/live-sessions/api/studentFeedback';
import { getCriterionAverages } from '@/live-sessions/api/sessions';
import { getUserHistory } from '@/api/criterias';
import { getCurrentUser, getProfessorUsers } from '@/api/users';
import { inject } from 'vue';
import { ChatBubbleLeftIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/solid';

export default {
  components: { BaseSelect, BaseInfoIcon, ChatBubbleLeftIcon, QuestionMarkCircleIcon },
  setup() {
    const chatEventBus = inject("chatEventBus", null);
    return { chatEventBus };
  },
  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      selectedSessionId: null,
      selectedSession: null,
      userSession: null,
      criterionAverages: [],
      userHistory: [],
      selectedCriterionId: null,
      refreshInterval: null,
      lastHistorySnapshot: [],
      lastUserSessionSnapshot: {},
      initialLoad: true,
      currentUser: null,
      errorMessage: null,
      errorTitle: null,
    };
  },

  computed: {
    sessionOptions() {
      return this.sessions.map(s => ({ value: s.id.toString(), label: s.title }));
    },
    criterionOptions() {
      if (!this.userHistory.length) return [];
      const uniqueCriteria = [...new Set(this.userHistory.map(h => h.criterion_name))];
      return [{ value: 'all', label: 'All criteria' }, ...uniqueCriteria.map(name => ({ value: name, label: name }))];
    },
    nonTextCriteria() {
      if (!this.selectedSession?.feedback) return [];
      return this.selectedSession.feedback
        .filter(c => c.criterion_type !== 'text')
        .sort((a, b) => a.criterion_name.localeCompare(b.criterion_name));
    },

    textCriteria() {
      if (!this.selectedSession?.feedback) return [];
      return this.selectedSession.feedback
        .filter(c => c.criterion_type === 'text')
        .sort((a, b) => a.criterion_name.localeCompare(b.criterion_name));
},
    filteredHistory() {
      if (this.selectedCriterionId === 'all' || !this.selectedCriterionId) return this.userHistory;
      return this.userHistory.filter(h => h.criterion_name === this.selectedCriterionId);
    }
  },

  watch: {
    async selectedSessionId(newSessionId) {
      if (!newSessionId) {
        this.selectedSession = null;
        return;
      }
      this.$router.replace({ query: { ...this.$route.query, session: newSessionId } });
      await this.selectSession(newSessionId);
    },
    selectedCriterionId(newCriterionId) {
      this.$router.replace({ query: { ...this.$route.query, criterion: newCriterionId } });
    },
  },

  methods: {
    formatGrade(grade) {
      if (grade == null) return '—';
      return Number(grade).toFixed(2);
    },

    async fetchData() {
      try {
        const { data } = await getLiveSessions();
        this.sessions = data;
        const sessionId = this.$route.query.session;
        if (sessionId) await this.selectSession(sessionId.toString());
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        this.sessions = [];
        this.selectedSession = null;
      }
    },

    getCriterionWeightForRole(criterion) {
      if (!criterion.criterion || !this.userSession?.role_id) return criterion.base_weight || 0;
      const roleWeight = criterion.criterion.role_weights?.find(rw => rw.role_id === this.userSession.role_id);
      return roleWeight ? roleWeight.weight : criterion.base_weight || 0;
    },

    async fetchCriterionAverages(sessionId) {
      if (!sessionId) {
        this.criterionAverages = [];
        return;
      }
      try {
        const res = await getCriterionAverages(sessionId);
        this.criterionAverages = res.data;
      } catch (err) {
        console.error('Failed to fetch criterion averages:', err);
        this.criterionAverages = [];
      }
    },

    showNotification(message) {
      if ("Notification" in window && Notification.permission === "granted") {
        const now = new Date().toLocaleString();
        new Notification(`${this.selectedSession.session_title} - ${now}`, { body: message, requireInteraction: true });
      }
    },

    diffHistory(oldHistory, newHistory) {
      const notifications = [];
      
      const oldKeys = new Set(oldHistory.map(e => `${e.criterion_name}_${e.changed_at}`));

      // Detect added entries
      newHistory.forEach(entry => {
        const key = `${entry.criterion_name}_${entry.changed_at}`;
        if (!oldKeys.has(key)) {
          let msg = '';

          if (entry.source === 'team') {
            msg += entry.team_name ? `[${entry.team_name}] ` : '[TEAM] ';
          }

          msg += `${entry.criterion_name}:`;

          if (entry.change_value !== undefined && entry.change_value !== null) {
            if (entry.change_value > 0) {
              msg += `▲ +${entry.change_value} `;
            } else if (entry.change_value < 0) {
              msg += `▼ ${entry.change_value} `;
            }
          }

          const parts = [];

          if (entry.int_value != null) parts.push(`New Value = ${entry.int_value}`);
          if (entry.bool_value != null) parts.push(`New Value = ${entry.bool_value}`);

          if (parts.length) msg += ' ' + parts.join(' ');

          if (entry.text_value) {
            if (parts.length) msg += '\n';
            msg += entry.text_value;
          }

          notifications.push(msg);
        }
      });

      return notifications;
    },

    diffFeedback(oldFeedback, newFeedback) {
      const notifications = [];
      if (!oldFeedback) return notifications;
      
      // Check for changes in actual feedback content (skip if new value is null)
      if (oldFeedback.grade !== newFeedback.grade && newFeedback.grade !== null) {
        notifications.push(`Grade updated: ${newFeedback.grade}`);
      }
      if (oldFeedback.goal !== newFeedback.goal && newFeedback.goal !== null) {
        notifications.push(`Educational goal updated to "${newFeedback.goal}"`);
      }
      if (oldFeedback.evaluation !== newFeedback.evaluation && newFeedback.evaluation !== null) {
        notifications.push(`Evaluation updated to "${newFeedback.evaluation}"`);
      }
      if (oldFeedback.recommendation !== newFeedback.recommendation && newFeedback.recommendation !== null) {
        notifications.push(`Recommendation updated to "${newFeedback.recommendation}"`);
      }
      
      return notifications;
    },

    async checkForUpdates(sessionId) {
      const userSessionRes = await getUserSession(sessionId);
      const newUserSession = userSessionRes.data;
      const teamIds = newUserSession.team_ids || [];
      const teamNames = newUserSession.team_names || [];
      
      let allHistory = [];
      
      if (teamIds.length === 0) {
        // User has no teams - fetch only user history
        const historyRes = await getUserHistory(sessionId, null);
        allHistory = historyRes.data;
      } else {
        // User has teams - fetch history for each team (includes user history + team history)
        const historyPromises = teamIds.map(teamId => getUserHistory(sessionId, teamId));
        const historyResults = await Promise.all(historyPromises);
        
        // Merge all history entries and add team names
        const allHistoryWithDuplicates = [];
        historyResults.forEach((res, index) => {
          const teamName = teamNames[index] || 'Unknown Team';
          const entries = res.data.map(entry => ({
            ...entry,
            team_name: entry.source === 'team' ? teamName : undefined
          }));
          allHistoryWithDuplicates.push(...entries);
        });
        
        // Remove duplicates based on id (user history appears in every team call)
        const seenIds = new Set();
        allHistory = allHistoryWithDuplicates.filter(entry => {
          if (seenIds.has(entry.id)) {
            return false;
          }
          seenIds.add(entry.id);
          return true;
        });
      }
      
      // Sort by timestamp
      allHistory.sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
      
      console.log('[LiveSessionsFeedback] Final history:', {
        total: allHistory.length,
        userEntries: allHistory.filter(e => e.source === 'user').length,
        teamEntries: allHistory.filter(e => e.source === 'team').length,
        sample: allHistory.slice(0, 3)
      });
      
      const [
        feedbackRes,
        averagesRes
      ] = await Promise.all([
        getSessionFeedback(sessionId, teamIds.length > 0 ? teamIds[0] : null),
        getCriterionAverages(sessionId)
      ]);

      const newHistory = allHistory;
      const newFeedback = feedbackRes.data;
      const newAverages = averagesRes.data;

      if (!this.initialLoad){
        this.diffHistory(this.lastHistorySnapshot, newHistory)
        .forEach(msg => this.showNotification(msg));

      this.diffFeedback(this.lastUserSessionSnapshot, newUserSession)
        .forEach(msg => this.showNotification(msg));
      }
      this.userHistory = newHistory;
      this.userSession = newUserSession;
      this.selectedSession = newFeedback;
      this.criterionAverages = newAverages;

      this.lastHistorySnapshot = JSON.parse(JSON.stringify(newHistory));
      this.lastUserSessionSnapshot = { ...newUserSession };

      this.initialLoad = false;
    },

    async selectSession(sessionId) {
      try {
        this.lastHistorySnapshot = [];
        this.lastUserSessionSnapshot = {};
        this.initialLoad = true;

        this.selectedSessionId = sessionId;

        const roleRes = await getUserSession(sessionId);
        this.userSession = roleRes.data;

        // Always fetch updates - the backend will handle what data to show
        await this.checkForUpdates(sessionId);
      } catch (err) {
        console.error('Failed to fetch session details:', err);
        this.selectedSession = null;
        this.userSession = null;
      }
    },

    async openChatWithHistory(historyEntry) {
      try {
        console.log("Opening chat with history entry:", historyEntry);
        
        // Get current user to determine their role
        if (!this.currentUser) {
          const response = await getCurrentUser();
          this.currentUser = response.data;
          console.log("Current user:", this.currentUser);
        }

        // Get the other user to chat with
        // Students chat with professors, professors chat with students
        let otherUserId = null;
        let otherUserName = "User";
        const userType = this.currentUser.user_type?.name?.toLowerCase();
        console.log("User type:", userType);

        if (userType === "student") {
          // Student: get professors from their credential
          try {
            const response = await getProfessorUsers();
            console.log("Professors response:", response);
            if (response.data && response.data.length > 0) {
              // Prefer the first professor (could be enhanced to find the specific one who made this rating)
              const prof = response.data[0];
              otherUserId = prof.id;
              otherUserName = `${prof.first_name} ${prof.last_name}`;
              console.log("Selected professor:", prof);
            } else {
              // No professors with access to this credential
              this.errorTitle = 'No Professors Available';
              this.errorMessage = `No professors have access to your Micro-Credential (${this.currentUser.micro_credential?.name || 'Unknown'}).\n\nPlease contact your administrator to grant at least one professor access to your credential to enable the chat feature.`;
              console.warn("No professors with access to credential:", this.currentUser.micro_credential_id);
              return;
            }
          } catch (err) {
            console.error("Failed to get professors:", err);
            this.errorTitle = 'Error Loading Professors';
            this.errorMessage = 'Failed to load professors. Please try again later.';
            return;
          }
        } else {
          // Professor: get the student who owns this session
          otherUserId = this.userSession?.user_id;
          console.log("Student user ID:", otherUserId, "from userSession:", this.userSession);
          if (this.userSession && otherUserId) {
            otherUserName = `Student ${otherUserId}`;
          } else {
            console.error("Could not determine student for professor chat");
            this.errorTitle = 'Cannot Open Chat';
            this.errorMessage = 'Could not determine which student to chat with. Make sure you are viewing a student\'s feedback page.';
            return;
          }
        }

        if (!otherUserId) {
          console.error("Could not determine other user for chat. userType:", userType, "otherUserId:", otherUserId);
          return;
        }

        console.log("Opening chat with user:", otherUserId, "name:", otherUserName);

        // Prepare history reference data with source
        const historyData = {
          ...historyEntry,
          source: historyEntry.source || "user",
        };

        // Emit event to open chat with this history
        if (this.chatEventBus) {
          console.log("Emitting openChatWithHistory event");
          this.chatEventBus.emit("openChatWithHistory", {
            userId: otherUserId,
            userName: otherUserName,
            history: historyData,
          });
        } else {
          console.error("Chat event bus not available");
        }
      } catch (err) {
        console.error("Error opening chat with history:", err);
        this.errorTitle = 'Error Opening Chat';
        this.errorMessage = 'An unexpected error occurred. Please try again.';
      }
    }
  },

  async mounted() {
    if ("Notification" in window && Notification.permission !== "granted") {
      await Notification.requestPermission();
    }

    // Load current user for chat functionality
    try {
      const response = await getCurrentUser();
      this.currentUser = response.data;
    } catch (err) {
      console.error('Failed to load current user:', err);
    }

    await this.fetchData();
    const criterionId = this.$route.query.criterion;
    this.selectedCriterionId = criterionId || 'all';

    this.refreshInterval = setInterval(async () => {
      if (this.selectedSessionId) await this.checkForUpdates(this.selectedSessionId);
    }, 5000);
  },

  beforeUnmount() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  },
};
</script>
